import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Table } from "react-bootstrap";

import supabase from "../utils/Supabase";
import { showToast } from "../utils/Toasts";
import { numberFormatter } from "../utils/Numbers";
import { getPlatScripts } from "../utils/GetPlatScripts";
import { applyFilter } from "../utils/ApplyFilter";
import { FilterContext } from "../utils/Contexts";
import { useDebounce } from "../utils/useDebounce";
import { sortScripts } from '../utils/SortScripts';

import useScriptsStore from "../stores/PlatScripts";

import Fireworks from "../components/Fireworks";
import { LoadingRing } from "../components/LoadingRing";
import { TypewriterCursor, TypewriterEffect } from "../components/TypewriterEffect";
import SortableHeader from "../components/SortableHeader";
import PageList from "../components/PageList";
import FileNameForceWrap from "../components/FileNameForceWrap";

import {
  SearchText,
} from "../components/SearchFields";

const ITEMS_PER_PAGE = 100;

function PlatinumDatabasePage() {
  const isSmall = useMediaQuery({ query: '(max-width: 576px)' });

  const [haveResults, setHaveResults] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  const totalScripts = useScriptsStore((state) => state.totalScripts);
  const setTotalScripts = useScriptsStore((state) => state.setTotalScripts);

  const [shownScripts, setShownScripts] = useState([]);

  const [search, setSearch] = useState({});
  const [filters, setFilters] = useState({});

  const [filteredScriptCount, setfilteredScriptCount] = useState(0);
  const [downloadingScriptId, setDownloadingScriptId] = useState<string | null>(
    null
  );

  const totalScriptsCount = useScriptsStore((state) => state.totalCount);
  const setTotalScriptsCount = useScriptsStore((state) => state.setTotalCount);

  const [searchParams, setSearchParams] = useSearchParams();

  const [contentStable, setContentStable] = useState(false);

  // Add a short delay via useDebounce before the filter is applied to prevent a typing lag in the UI
  const debouncedFilters = useDebounce(filters, 300);

  const sortConfig = useScriptsStore((state) => state.sortConfig);
  const setSortConfig = useScriptsStore((state) => state.setSortConfig);
  const sortedScripts = useMemo(() => sortScripts(totalScripts, sortConfig), [totalScripts, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key, direction: 'asc' };
    });
  };

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const results = applyFilter(totalScripts, debouncedFilters);
    setfilteredScriptCount(results.length);
    setShownScripts(
      results.length > ITEMS_PER_PAGE ?
        results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
        : results
    );
    setCurrentPage(1); // Reset to first page on filter change

    const filteredSearchParams = (Object.fromEntries(
      Object.entries(debouncedFilters)
        .filter(([key, value]) => value && value !== "" && value !== key && !key.includes("Date") && !key.includes("_includeEmpty"))
        .map(([key, value]) => [key, String(value)])
    ));

    // Only set haveResults to true after initial load is complete and filtering is done
    if (initialLoadComplete) {
      setSearchParams(filteredSearchParams);
      setHaveResults(true);

      setTimeout(() => {
        setContentStable(true);
      }, 100);
    }
  }, [totalScripts, debouncedFilters, initialLoadComplete, setSearchParams]);

  useEffect(() => {

    function initializePlatScripts(scripts) {
      setTotalScriptsCount(scripts.length);

      // Handle search params
      const newFilters = { ...filters };
      searchParams.forEach((value, key) => {
        setSearch({ ...search, [key]: value });
        newFilters[key] = value;
      });
      setFilters(newFilters);

      // Mark initial load as complete
      setInitialLoadComplete(true);
    }

    if (totalScripts.length === 0) {
      getPlatScripts().then((data) => {
        if (data) {
          const sortedData = sortScripts(data, sortConfig);
          setTotalScripts(sortedData as []);
          initializePlatScripts(sortedData);
        }
      });
    } else {
      initializePlatScripts(totalScripts);
    }
  }, [searchParams]);

  useEffect(() => {
    const results = applyFilter(sortedScripts, filters);
    
    setShownScripts(
      results.length > ITEMS_PER_PAGE ?
        results.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
        : results
    );
  }, [currentPage, sortConfig]);

  return (
    <>
    
      <title>Script Hive - Platinum Files</title>
      <Fireworks />
      <div className="hive-container d-flex" style={{ background: 'transparent' }}>
        
        <h1 className="mt-3 mx-auto" style={{color: 'white'}}>Platinum Scripts</h1>
        <main>
          
          {/* Pass filters and setFilters to all child components with UseContext */}
          <FilterContext value={{ filters: filters, setFilters: setFilters, search: search }}>
            {haveResults ? (
              <div className="px-3 px-md-5">
                <SearchText
                  fieldName="name"
                  placeholder="Search by filename"
                />
              </div>
            ) : (
              <LoadingRing />
            )}
          </FilterContext>

          <div className="mx-3 mx-md-5">
            {haveResults && (
              <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2 text-center text-md-start">
                <p style={{color:'white'}}>
                  {contentStable ? (
                    <TypewriterEffect speed={50}>
                      {numberFormatter(shownScripts.length)} out of{" "}
                      {shownScripts.length > filteredScriptCount
                        ? numberFormatter(totalScriptsCount)
                        : numberFormatter(filteredScriptCount)}{" "}
                      files shown
                    </TypewriterEffect>
                  ) : (
                    <TypewriterCursor />
                  )}
                </p>

                <p style={{color:'white'}}>
                  Got questions or takedown requests? Please{" "}
                  <a href="mailto:support@scripthive.com">
                    email us
                  </a>
                  .
                </p>
              </div>
            )}
          </div>

          <div className="mx-3 mx-md-5 d-flex justify-content-center justify-content-md-start">
            <PageList
              totalCount={filters ? filteredScriptCount : totalScriptsCount}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              className="m-0"
            />
          </div>

          <div className="script-table table-container mt-3 mb-4 mx-3 mx-md-5">
            {haveResults ? (
              <div className="table-responsive">
                <Table striped hover size="sm" variant="light" className="mb-5">
                  <thead>
                    <tr>
                      <SortableHeader
                        label="Filename"
                        sortKey="name"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                      />
                      <SortableHeader
                        label="Date Added"
                        sortKey="created_at"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        width={isSmall ? 120 : 240}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {shownScripts.map((script) => (

                      <tr key={script.id}>
                        <td className="h6">
                          <button
                            type="button"
                            className="btn btn-link p-0 m-0 align-baseline text-break"
                            style={{
                              textDecoration: "underline",
                              color: "#0d6efd",
                              background: "none",
                              border: "none",
                              cursor:
                                downloadingScriptId === script.id
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                            disabled={downloadingScriptId === script.id}
                            onClick={async () => {
                              if (downloadingScriptId) return;
                              setDownloadingScriptId(script.id);

                              const clickedScript = shownScripts.find(
                                (s) => s.id === script.id
                              );
                              if (!clickedScript) {
                                showToast("error", "Script not found.");
                                setDownloadingScriptId(null);
                                return;
                              }

                              const filePath = clickedScript.name;

                              try {
                                const { data, error } = await supabase.storage
                                  .from("platinum-files")
                                  .download(filePath);

                                if (error || !data) {
                                  showToast(
                                    "error",
                                    "Error downloading file. Please try again."
                                  );
                                  setDownloadingScriptId(null);
                                  return;
                                }

                                // Create and trigger download
                                const blob = new Blob([data], {
                                  type: "application/pdf",
                                });
                                const link = document.createElement("a");
                                link.href = window.URL.createObjectURL(blob);
                                link.download = filePath;
                                link.click();
                                window.URL.revokeObjectURL(link.href);

                              } catch (unexpectedError) {
                                showToast("error",
                                  "An unexpected error occurred. Please try again."
                                );
                              } finally {
                                setDownloadingScriptId(null);
                              }
                            }}
                          >
                            <FileNameForceWrap filename={script.name} />
                          </button>
                        </td>
                        <td>
                          {isSmall ? new Date(script.created_at).toLocaleDateString() : new Date(script.created_at).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : ""}
          </div>
        </main>
      </div>
    </>
  );
}
export default PlatinumDatabasePage;